import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MapPin, ShieldCheck, Truck, Wallet, CreditCard, Lock, Ticket, Sparkles, Check, X, Copy } from 'lucide-react';
import { usePlugins } from '../context/PluginContext';
import { useAuth } from '../context/AuthContext';
import { api, createOrder, removeFromCartItems, validateCoupon } from '../api/client';
import { isRTL, getTranslation as t, translateError as te } from '../utils/translations';
import DynamicSectionsRenderer from '../components/DynamicSectionsRenderer';

const PAYMENT_SETTINGS_PLUGIN_ID = 'payment-settings';

const DEFAULT_PAYMENT_CONFIG = {
    codEnabled: true,
    walletEnabled: false,
    wallets: [],
    bankTransferEnabled: false,
    banks: [],
    cardGatewayEnabled: false,
    gatewayProvider: 'bank_direct',
    enableMada: true,
    enableVisaMastercard: true,
    enableApplePay: true,
    paypalEnabled: false,
    tabbyEnabled: true,
    tamaraEnabled: true,
    countryOverrides: {}
};

const COUNTRY_MAP = {
    'saudi arabia': 'SA', 'ksa': 'SA', 'sa': 'SA', 'المملكة العربية السعودية': 'SA', 'السعودية': 'SA',
    'united arab emirates': 'AE', 'uae': 'AE', 'ae': 'AE', 'الإمارات العربية المتحدة': 'AE', 'الإمارات': 'AE', 'دبي': 'AE',
    'egypt': 'EG', 'eg': 'EG', 'جمهورية مصر العربية': 'EG', 'مصر': 'EG',
    'jordan': 'JO', 'jo': 'JO', 'المملكة الأردنية الهاشمية': 'JO', 'الأردن': 'JO',
    'kuwait': 'KW', 'kw': 'KW', 'دولة الكويت': 'KW', 'الكويت': 'KW',
    'qatar': 'QA', 'qa': 'QA', 'دولة قطر': 'QA', 'قطر': 'QA',
    'bahrain': 'BH', 'bh': 'BH', 'مملكة البحرين': 'BH', 'البحرين': 'BH',
    'oman': 'OM', 'om': 'OM', 'سلطنة عمان': 'OM', 'عمان': 'OM',
    'united states': 'US', 'usa': 'US', 'us': 'US', 'الولايات المتحدة': 'US', 'الولايات المتحدة الأمريكية': 'US', 'أمريكا': 'US',
    'united kingdom': 'GB', 'uk': 'GB', 'gb': 'GB', 'المملكة المتحدة': 'GB', 'بريطانيا': 'GB',
    'germany': 'DE', 'de': 'DE', 'ألمانيا': 'DE', 'deutschland': 'DE',
    'france': 'FR', 'fr': 'FR', 'فرنسا': 'FR',
    'canada': 'CA', 'ca': 'CA', 'كندا': 'CA',
    'turkey': 'TR', 'tr': 'TR', 'تركيا': 'TR',
    'yemen': 'YE', 'ye': 'YE', 'اليمن': 'YE', 'الجمهورية اليمنية': 'YE',
    'somalia': 'SO', 'so': 'SO', 'الصومال': 'SO', 'جمهورية الصومال': 'SO',
    'somaliland': 'SO', 'sml': 'SO', 'أرض الصومال': 'SO',
    'ethiopia': 'ET', 'et': 'ET', 'إثيوبيا': 'ET', 'جمهورية إثيوبيا': 'ET'
};

const resolveCountryCode = (countryName) => {
    if (!countryName) return 'SA';
    const clean = countryName.trim().toLowerCase();
    if (COUNTRY_MAP[clean]) return COUNTRY_MAP[clean];
    for (const [key, code] of Object.entries(COUNTRY_MAP)) {
        if (clean.includes(key)) return code;
    }
    return clean.length === 2 ? clean.toUpperCase() : 'SA';
};

const COUNTRY_CODE_TO_LABEL = {
    SA: { nameAr: 'المملكة العربية السعودية', nameEn: 'Saudi Arabia', flag: '🇸🇦' },
    AE: { nameAr: 'الإمارات العربية المتحدة', nameEn: 'United Arab Emirates', flag: '🇦🇪' },
    YE: { nameAr: 'الجمهورية اليمنية', nameEn: 'Yemen', flag: '🇾🇪' },
    SO: { nameAr: 'جمهورية الصومال الفيدرالية', nameEn: 'Somalia', flag: '🇸🇴' },
    ET: { nameAr: 'جمهورية إثيوبيا الفيدرالية', nameEn: 'Ethiopia', flag: '🇪🇹' },
    EG: { nameAr: 'جمهورية مصر العربية', nameEn: 'Egypt', flag: '🇪🇬' },
    JO: { nameAr: 'المملكة الأردنية الهاشمية', nameEn: 'Jordan', flag: '🇯🇴' },
    KW: { nameAr: 'دولة الكويت', nameEn: 'Kuwait', flag: '🇰🇼' },
    QA: { nameAr: 'دولة قطر', nameEn: 'Qatar', flag: '🇶🇦' },
    BH: { nameAr: 'مملكة البحرين', nameEn: 'Bahrain', flag: '🇧🇭' },
    OM: { nameAr: 'سلطنة عُمان', nameEn: 'Oman', flag: '🇴🇲' },
    US: { nameAr: 'الولايات المتحدة الأمريكية', nameEn: 'United States', flag: '🇺🇸' },
    GB: { nameAr: 'المملكة المتحدة (بريطانيا)', nameEn: 'United Kingdom', flag: '🇬🇧' },
    DE: { nameAr: 'جمهورية ألمانيا الاتحادية', nameEn: 'Germany', flag: '🇩🇪' },
    FR: { nameAr: 'الجمهورية الفرنسية', nameEn: 'France', flag: '🇫🇷' }
};

const resolveEffectivePaymentConfig = (baseConfig, countryCode) => {
    const base = baseConfig || DEFAULT_PAYMENT_CONFIG;
    const overrides = base.countryOverrides && base.countryOverrides[countryCode];
    if (overrides && overrides.customEnabled !== false) {
        return {
            ...base,
            ...overrides,
            wallets: overrides.wallets !== undefined ? overrides.wallets : (base.wallets || []),
            banks: overrides.banks !== undefined ? overrides.banks : (base.banks || [])
        };
    }
    // Regional smart fallback if not explicitly customized
    if (countryCode === 'EG') {
        return {
            ...base,
            enableMada: false,
            enableApplePay: true,
            enableVisaMastercard: true,
            cardGatewayEnabled: true,
            walletEnabled: true,
            wallets: [
                { provider: 'فودافون كاش (Vodafone Cash)', number: '+20 10 1234 5678', holder: 'لوكسفونو مصر' },
                { provider: 'انستاباي (InstaPay)', number: 'luxvono@instapay', holder: 'Luxvono Egypt' }
            ],
            bankTransferEnabled: true,
            banks: [
                { name: 'البنك التجاري الدولي (CIB)', account: '10002938475', iban: 'EG38001000000000010002938475', holder: 'Luxvono Egypt LLC' }
            ],
            paypalEnabled: false,
            tabbyEnabled: false,
            tamaraEnabled: false,
            codEnabled: true
        };
    } else if (countryCode === 'JO') {
        return {
            ...base,
            enableMada: false,
            cardGatewayEnabled: true,
            walletEnabled: true,
            wallets: [
                { provider: 'كليك (CliQ)', number: 'LUXVONO_JO', holder: 'Luxvono Jordan' }
            ],
            bankTransferEnabled: true,
            paypalEnabled: false,
            tabbyEnabled: false,
            tamaraEnabled: false,
            codEnabled: true
        };
    } else if (countryCode === 'US') {
        return {
            ...base,
            enableMada: false,
            enableApplePay: true,
            cardGatewayEnabled: true,
            walletEnabled: false,
            paypalEnabled: true,
            tabbyEnabled: false,
            tamaraEnabled: false,
            codEnabled: false,
            bankTransferEnabled: true
        };
    }
    return base;
};

const STEPS = [
    { id: 'details', label: 'Customer details' },
    { id: 'delivery', label: 'Delivery & trust' },
    { id: 'payment', label: 'Payment review' }
];

const CheckoutPage = ({ cart, onComplete, storeSettings }) => {
    const navigate = useNavigate();
    const { getPluginConfig, pluginsLoaded } = usePlugins();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [paymentConfig, setPaymentConfig] = useState(DEFAULT_PAYMENT_CONFIG);
    const [error, setError] = useState('');
    const [currentStep, setCurrentStep] = useState('details');
    const [allProducts, setAllProducts] = useState([]);
    const [allCategories, setAllCategories] = useState([]);

    useEffect(() => {
        const loadCheckoutContext = async () => {
            try {
                const [productsRes, categoriesRes] = await Promise.all([
                    import('../api/client').then(m => m.getProducts(0, 50)),
                    import('../api/client').then(m => m.getCategories())
                ]);
                setAllProducts(productsRes?.data || []);
                setAllCategories(categoriesRes || []);
            } catch (e) {
                console.warn('Failed to load checkout recommendations:', e);
            }
        };
        loadCheckoutContext();
    }, []);

    const [form, setForm] = useState({
        customerName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || user.email?.split('@')[0] || '' : '',
        customerPhone: user?.phone || '',
        backupPhone: '',
        street: user?.address?.street || '',
        city: user?.address?.city || '',
        country: user?.address?.country || 'United States',
        paymentMethod: 'CARD_PAYMENT',
        receiptFile: null
    });

    useEffect(() => {
        if (user) {
            setForm(prev => ({
                ...prev,
                customerName: prev.customerName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || user.email?.split('@')[0] || '',
                customerPhone: prev.customerPhone || user.phone || '',
                street: prev.street || user.address?.street || prev.street,
                city: prev.city || user.address?.city || prev.city,
                country: prev.country || user.address?.country || prev.country
            }));
        }
    }, [user]);

    const [cardDetails, setCardDetails] = useState({
        cardholderName: '',
        cardNumber: '',
        expiry: '',
        cvv: ''
    });

    const [copiedKey, setCopiedKey] = useState(null);

    const copyToClipboard = (text, key) => {
        if (!text) return;
        try {
            navigator.clipboard.writeText(text);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 2500);
        } catch (_) {}
    };

    const [isRtl, setIsRtl] = useState(() => isRTL());
    const [privacyShieldActive, setPrivacyShieldActive] = useState(false);
    const [screenshotBlockedToast, setScreenshotBlockedToast] = useState(false);

    useEffect(() => {
        const handleLangChange = () => {
            setIsRtl(isRTL());
        };
        window.addEventListener('luxvono-language-changed', handleLangChange);
        window.addEventListener('storage', handleLangChange);
        return () => {
            window.removeEventListener('luxvono-language-changed', handleLangChange);
            window.removeEventListener('storage', handleLangChange);
        };
    }, []);

    // Anti-Screenshot, Anti-Print & Privacy Shield Security Guard
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Block PrintScreen, Ctrl+P, Ctrl+S, Ctrl+Shift+S, F12, Ctrl+Shift+I, Win/Cmd+Shift+S
            const isPrintScreen = e.key === 'PrintScreen' || e.keyCode === 44;
            const isPrint = (e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P');
            const isSave = (e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S');
            const isSnipping = (e.ctrlKey || e.metaKey || e.shiftKey) && (e.key === 's' || e.key === 'S' || e.key === '3' || e.key === '4' || e.key === '5');
            const isDevTools = e.key === 'F12' || ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'i' || e.key === 'I' || e.key === 'c' || e.key === 'C'));

            if (isPrintScreen || isPrint || isSave || isDevTools) {
                e.preventDefault();
                e.stopPropagation();
                try {
                    navigator.clipboard?.writeText?.('');
                } catch (_) {}
                setScreenshotBlockedToast(true);
                setTimeout(() => setScreenshotBlockedToast(false), 4500);
            }
        };

        const handleKeyUp = (e) => {
            if (e.key === 'PrintScreen' || e.keyCode === 44) {
                try {
                    navigator.clipboard?.writeText?.('');
                } catch (_) {}
                setScreenshotBlockedToast(true);
                setTimeout(() => setScreenshotBlockedToast(false), 4500);
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                setPrivacyShieldActive(true);
            }
        };

        const handleWindowBlur = () => {
            setPrivacyShieldActive(true);
        };

        const handleWindowFocus = () => {
            setPrivacyShieldActive(false);
        };

        window.addEventListener('keydown', handleKeyDown, true);
        window.addEventListener('keyup', handleKeyUp, true);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleWindowBlur);
        window.addEventListener('focus', handleWindowFocus);

        // Inject print-blocking stylesheet
        const printStyle = document.createElement('style');
        printStyle.id = 'luxvono-checkout-print-shield';
        printStyle.innerHTML = '@media print { body { display: none !important; } }';
        document.head.appendChild(printStyle);

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            window.removeEventListener('keyup', handleKeyUp, true);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleWindowBlur);
            window.removeEventListener('focus', handleWindowFocus);
            const el = document.getElementById('luxvono-checkout-print-shield');
            if (el) el.remove();
        };
    }, []);

    const formatCardNumber = (val) => {
        const digits = (val || '').replace(/\D/g, '').slice(0, 16);
        const chunks = digits.match(/.{1,4}/g) || [];
        return chunks.join(' ');
    };

    const formatExpiryDate = (val) => {
        const digits = (val || '').replace(/\D/g, '').slice(0, 4);
        if (digits.length >= 3) {
            return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        return digits;
    };

    const subtotal = useMemo(
        () => (cart || []).reduce((sum, item) => sum + ((item.price || item.currentPrice || 0) * (item.quantity || item.qty || 1)), 0),
        [cart]
    );
    const shipping = cart.length > 0 && subtotal < 750 ? 24 : 0;
    const estimatedTax = 0;

    const [appliedCoupon, setAppliedCoupon] = useState(() => {
        try {
            const raw = localStorage.getItem('luxvono_active_coupon');
            return raw ? JSON.parse(raw) : null;
        } catch (_) {
            return null;
        }
    });
    const [checkoutCouponCode, setCheckoutCouponCode] = useState('');
    const [couponFeedback, setCouponFeedback] = useState('');

    const discountAmount = useMemo(() => {
        if (!appliedCoupon) return 0;
        if (appliedCoupon.discountType === 'PERCENTAGE') {
            const pctVal = (subtotal * (Number(appliedCoupon.discountValue) || 0)) / 100;
            return appliedCoupon.maxDiscountCap ? Math.min(pctVal, Number(appliedCoupon.maxDiscountCap)) : pctVal;
        }
        if (appliedCoupon.discountType === 'FIXED_AMOUNT') {
            return Math.min(subtotal, Number(appliedCoupon.discountValue || appliedCoupon.discountAmount || 0));
        }
        if (appliedCoupon.discountType === 'FREE_SHIPPING') {
            return shipping;
        }
        return Number(appliedCoupon.discountAmount) || 0;
    }, [appliedCoupon, subtotal, shipping]);

    const total = Math.max(0, subtotal - discountAmount + (appliedCoupon?.discountType === 'FREE_SHIPPING' ? 0 : shipping) + estimatedTax);

    useEffect(() => {
        if (!cart || cart.length === 0) {
            navigate('/cart');
            return;
        }

        if (pluginsLoaded) {
            const config = getPluginConfig(PAYMENT_SETTINGS_PLUGIN_ID);
            if (config?.paymentSettings) {
                setPaymentConfig(config.paymentSettings);
            }
            setLoading(false);
        }
    }, [cart, navigate, pluginsLoaded, getPluginConfig]);

    const activeCountryCode = useMemo(() => resolveCountryCode(form.country), [form.country]);
    const activePaymentConfig = useMemo(() => resolveEffectivePaymentConfig(paymentConfig, activeCountryCode), [paymentConfig, activeCountryCode]);

    // Automatically adapt paymentMethod whenever the active country changes
    useEffect(() => {
        const available = [];
        if (activePaymentConfig.enableMada) available.push('MADA');
        if (activePaymentConfig.enableApplePay) available.push('APPLE_PAY');
        if (activePaymentConfig.tabbyEnabled !== false) available.push('TABBY');
        if (activePaymentConfig.tamaraEnabled !== false) available.push('TAMARA');
        if (activePaymentConfig.cardGatewayEnabled !== false && activePaymentConfig.enableVisaMastercard !== false) available.push('CARD_PAYMENT');
        if (activePaymentConfig.walletEnabled) available.push('WALLET_PAYMENT');
        if (activePaymentConfig.bankTransferEnabled) available.push('BANK_TRANSFER');
        if (activePaymentConfig.paypalEnabled) available.push('PAYPAL');
        if (activePaymentConfig.codEnabled) available.push('COD');

        if (available.length > 0 && !available.includes(form.paymentMethod)) {
            setForm(prev => ({ ...prev, paymentMethod: available[0] }));
        }
    }, [activePaymentConfig]);

    const deliveryWindow = useMemo(() => {
        const locale = isRtl ? 'ar-EG' : 'en-US';
        const formatter = new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric' });
        const start = new Date();
        start.setDate(start.getDate() + 3);
        const end = new Date();
        end.setDate(end.getDate() + 6);
        const formattedStart = formatter.format(start);
        const formattedEnd = formatter.format(end);
        return {
            start: formattedStart,
            end: formattedEnd,
            display: isRtl ? `${formattedStart} - ${formattedEnd}` : `${formattedStart} - ${formattedEnd}`
        };
    }, [isRtl]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm(previous => ({ ...previous, [name]: value }));
    };

    const handleFileChange = (event) => {
        if (event.target.files && event.target.files.length > 0) {
            setForm(previous => ({ ...previous, receiptFile: event.target.files[0] }));
        }
    };

    const isDetailsValid = form.customerName.trim().length >= 2 && form.customerPhone.trim().length >= 6;
    const isDeliveryValid = form.street.trim().length >= 2 && form.city.trim().length >= 2 && form.country.trim().length >= 2;

    const goToNextStep = () => {
        setError('');
        if (currentStep === 'details' && !isDetailsValid) {
            setError(isRtl ? 'يرجى إدخال الاسم الكامل ورقم الهاتف للمتابعة.' : 'Please enter your full name and phone number to continue.');
            return;
        }
        if (currentStep === 'delivery' && !isDeliveryValid) {
            setError(isRtl ? 'يرجى إكمال عنوان الشحن (الشارع والمدينة والدولة) للمتابعة.' : 'Please complete your shipping address to continue.');
            return;
        }

        const currentIndex = STEPS.findIndex(step => step.id === currentStep);
        const nextStep = STEPS[currentIndex + 1];
        if (nextStep) setCurrentStep(nextStep.id);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');

        if (!isDetailsValid) {
            setCurrentStep('details');
            setError(isRtl ? 'يرجى إكمال بيانات العميل (الاسم ورقم الهاتف) أولاً.' : 'Please complete your customer details (Name & Phone) first.');
            return;
        }

        if (!isDeliveryValid) {
            setCurrentStep('delivery');
            setError(isRtl ? 'يرجى إكمال عنوان التوصيل والشحن (الشارع والمدينة والدولة) أولاً.' : 'Please complete your delivery address first.');
            return;
        }

        if (!form.paymentMethod) {
            setError(isRtl ? 'يرجى اختيار طريقة الدفع المناسبة.' : 'Please select a valid payment method.');
            return;
        }

        if (form.paymentMethod === 'BANK_TRANSFER' && !form.receiptFile) {
            setError(isRtl ? 'يرجى إرفاق إشعار التحويل البنكي لتأكيد الطلب.' : te('Please upload a proof of payment receipt for the Bank Transfer.'));
            return;
        }

        setSubmitting(true);
        try {
            let receiptImageUrl = '';

            if ((form.paymentMethod === 'BANK_TRANSFER' || form.paymentMethod === 'WALLET_PAYMENT') && form.receiptFile) {
                const formData = new FormData();
                formData.append('file', form.receiptFile);
                const uploadRes = await api.post('/api/v1/orders/upload-receipt', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (uploadRes.data?.success) {
                    receiptImageUrl = uploadRes.data.data;
                } else {
                    throw new Error('Failed to upload receipt');
                }
            }

            const payload = {
                items: cart.map(item => ({
                    productId: item.productId || item.id,
                    quantity: item.quantity || item.qty || 1,
                    price: item.price || item.currentPrice || 0
                })),
                shippingAddress: {
                    street: form.street,
                    city: form.city,
                    state: 'N/A',
                    country: form.country,
                    zipCode: 'N/A'
                },
                paymentMethod: form.paymentMethod,
                customerName: form.customerName,
                customerPhone: form.backupPhone ? `${form.customerPhone} / ${form.backupPhone}` : form.customerPhone,
                orderNotes: appliedCoupon?.code ? `Applied Promo: ${appliedCoupon.code} (Discount: $${discountAmount})` : '',
                couponCode: appliedCoupon?.code || '',
                discountAmount: discountAmount || 0,
                receiptImageUrl
            };

            await createOrder(payload);
            localStorage.removeItem('luxvono_active_coupon');

            const itemIds = cart.map(item => item.cartItemId).filter(Boolean);
            if (itemIds.length > 0) {
                try {
                    await removeFromCartItems(itemIds);
                } catch (clearError) {
                    console.warn('Failed to clear cart remotely:', clearError);
                }
            }

            setSubmitting(false);
            onComplete();
            navigate('/orders', { replace: true });
        } catch (requestError) {
            console.error('Checkout failed', requestError);
            setError(te(requestError.response?.data?.message || requestError.message || 'Checkout failed.'));
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="container" style={{ paddingTop: '100px', display: 'flex', justifyContent: 'center', minHeight: '60vh' }}>
                <span className="spin" style={{ display: 'inline-block', width: 32, height: 32, border: '3px solid rgba(0,0,0,0.1)', borderTopColor: 'var(--lux-purple)', borderRadius: '50%' }} />
            </div>
        );
    }

    return (
        <div 
            className="page-checkout" 
            style={{ position: 'relative', userSelect: 'none', WebkitUserSelect: 'none' }}
            onContextMenu={(e) => e.preventDefault()}
        >
            {/* Privacy Shield Overlay during window blur or capture attempt */}
            {privacyShieldActive && (
                <div 
                    onClick={() => setPrivacyShieldActive(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 99999,
                        background: 'rgba(0, 0, 0, 0.95)',
                        backdropFilter: 'blur(30px)',
                        WebkitBackdropFilter: 'blur(30px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        cursor: 'pointer',
                        padding: 24,
                        textAlign: 'center'
                    }}
                >
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                        <Lock size={32} color="#FFFFFF" />
                    </div>
                    <h3 style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 8 }}>
                        {isRtl ? 'حماية أمنية مشددة لبيانات الدفع' : 'Security Privacy Shield Active'}
                    </h3>
                    <p style={{ maxWidth: 450, fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: 20 }}>
                        {isRtl ? 'تم حجب بيانات الدفع مؤقتاً لمنع التقاط الشاشة وحماية سرية بطاقتك. انقر في أي مكان للمتابعة بأمان.' : 'Payment details are masked to prevent unauthorized screen captures. Click anywhere to resume securely.'}
                    </p>
                    <span style={{ fontSize: 13, background: 'rgba(255, 255, 255, 0.2)', border: '1px solid #FFFFFF', color: '#FFFFFF', padding: '8px 24px', borderRadius: 20, fontWeight: 600 }}>
                        {isRtl ? 'انقر هنا لاستئناف الدفع' : 'Click to resume checkout'}
                    </span>
                </div>
            )}

            {/* Anti-Screenshot Blocked Toast */}
            {screenshotBlockedToast && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 100000,
                        background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
                        border: '1.5px solid #ef4444',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
                        borderRadius: 16,
                        padding: '14px 24px',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        animation: 'fadeIn 0.3s ease'
                    }}
                >
                    <span style={{ fontSize: 22 }}>🚫</span>
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: 14, color: '#ef4444' }}>
                            {isRtl ? 'ممنوع التقاط الشاشة (Screenshot Blocked)' : 'Screenshot Blocked'}
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                            {isRtl ? 'يُمنع تصوير أو نسخ شاشة الدفع لحماية بطاقتك وأمان بياناتك المصرفية.' : 'Screen capture and copying are prohibited on the checkout page to protect your financial data.'}
                        </div>
                    </div>
                </div>
            )}

            <div className="container">
                <DynamicSectionsRenderer
                    page="checkout-page"
                    position="top"
                    storeSettings={storeSettings}
                    allProducts={allProducts}
                    allCategories={allCategories}
                />

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, fontSize: 13 }}>
                    <Link to="/cart" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>{t('CART') || (isRtl ? 'السلة' : 'Cart')}</Link>
                    <span style={{ color: 'var(--border)', margin: '0 4px' }}>&gt;</span>
                    <span style={{ color: '#FFFFFF', fontWeight: 600 }}>{isRtl ? 'إتمام الطلب' : 'Checkout'}</span>
                </div>

                <div className="section-header" style={{ marginBottom: '16px' }}>
                    <div>
                        <div className="section-kicker" style={{ fontSize: 12, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <ShieldCheck size={14} color="#10b981" />
                            <span>{isRtl ? 'إتمام الشراء المشفر والمحمي من التقاط الشاشة (PCI-DSS Anti-Capture)' : 'Premium Encrypted & Anti-Capture Checkout'}</span>
                        </div>
                        <h1 className="section-title" style={{ fontSize: '1.75rem', margin: '0 0 6px 0' }}>{isRtl ? 'إتمام الطلب والاقتناء' : t('CHECKOUT')}</h1>
                        <p className="section-subtitle" style={{ fontSize: '0.9rem', margin: 0 }}>
                            {isRtl ? 'تجربة شراء راقية ومحمية بأعلى بروتوكولات الأمان العالمية ومكافحة تصوير الشاشة وتسليم فاخر.' : 'A guided luxury flow with stable totals, delivery clarity, and payment confirmation.'}
                        </p>
                    </div>
                </div>

                {error && (
                    <div style={{ padding: 15, background: 'rgba(231, 76, 60, 0.08)', color: '#b84040', border: '1px solid rgba(231, 76, 60, 0.18)', borderRadius: 18, marginBottom: 20 }}>
                        {error}
                    </div>
                )}

                <div className="checkout-shell">
                    <form onSubmit={handleSubmit} className="checkout-left">
                        <div className="checkout-stepper">
                            {STEPS.map((step, idx) => {
                                const stepTitles = [
                                    { title: isRtl ? 'بيانات العميل' : 'Customer Details', sub: isRtl ? 'معلومات التواصل' : 'Contact info' },
                                    { title: isRtl ? 'الشحن والتوصيل' : 'Delivery & Trust', sub: isRtl ? 'العنوان والضمان' : 'Shipping address' },
                                    { title: isRtl ? 'طريقة الدفع' : 'Payment Review', sub: isRtl ? 'تأكيد الدفع والأمان' : 'Review & confirm' }
                                ];
                                const s = stepTitles[idx] || { title: step.label, sub: '' };
                                return (
                                    <button
                                        key={step.id}
                                        type="button"
                                        className={`checkout-step ${currentStep === step.id ? 'active' : ''}`}
                                        onClick={() => setCurrentStep(step.id)}
                                        style={{ textAlign: isRtl ? 'right' : 'left' }}
                                    >
                                        <strong style={{ display: 'block', fontSize: 13, letterSpacing: isRtl ? 'normal' : '0.12em' }}>{s.title}</strong>
                                        <span style={{ display: 'block', fontSize: 12, opacity: 0.85, marginTop: 4 }}>{s.sub}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {currentStep === 'details' && (
                            <section className="checkout-panel">
                                <h2>{isRtl ? 'بيانات العميل ومعلومات التواصل' : 'Customer details'}</h2>
                                <p className="checkout-panel-copy">
                                    {isRtl ? 'يرجى إدخال معلومات التواصل لضمان تسليم الشحنة الفاخرة بأعلى درجات الدقة والعناية.' : 'Capture the core information needed for a smooth luxury fulfillment handoff.'}
                                </p>
                                <div className="checkout-grid">
                                    <div className="checkout-field">
                                        <label htmlFor="customerName">{isRtl ? 'الاسم الكامل للعميل *' : t('FULL_NAME_REQUIRED')}</label>
                                        <input 
                                            id="customerName" 
                                            className="checkout-input" 
                                            name="customerName" 
                                            value={form.customerName} 
                                            onChange={handleChange} 
                                            autoComplete="name" 
                                            placeholder={isRtl ? 'مثال: محمد عبدالله' : 'e.g. Alexander Vance'} 
                                        />
                                    </div>
                                    <div className="checkout-field">
                                        <label htmlFor="customerPhone">{isRtl ? 'رقم التواصل الأساسي *' : t('PHONE_REQUIRED')}</label>
                                        <input 
                                            id="customerPhone" 
                                            className="checkout-input" 
                                            name="customerPhone" 
                                            value={form.customerPhone} 
                                            onChange={handleChange} 
                                            autoComplete="tel" 
                                            placeholder={isRtl ? 'مثال: +966 50 123 4567' : 'e.g. +1 555-0198'} 
                                            dir="ltr" 
                                        />
                                    </div>
                                    <div className="checkout-field checkout-field--full">
                                        <label htmlFor="backupPhone">{isRtl ? 'رقم تواصل احتياطي (اختياري)' : t('BACKUP_PHONE')}</label>
                                        <input 
                                            id="backupPhone" 
                                            className="checkout-input" 
                                            name="backupPhone" 
                                            value={form.backupPhone} 
                                            onChange={handleChange} 
                                            autoComplete="tel-national" 
                                            placeholder={isRtl ? 'رقم هاتف إضافي للتسليم' : 'Optional secondary phone'} 
                                            dir="ltr" 
                                        />
                                    </div>
                                </div>
                            </section>
                        )}

                        {currentStep === 'delivery' && (
                            <section className="checkout-panel">
                                <h2>{isRtl ? 'عنوان الشحن والتسليم الفاخر' : 'Delivery and trust'}</h2>
                                <p className="checkout-panel-copy">
                                    {isRtl ? 'حدد موقع استلام الشحنة لضمان وصول مندوب الكونسيرج والتوصيل المباشر.' : 'Keep the address input compact while making delivery expectations and protections explicit.'}
                                </p>
                                <div className="checkout-grid">
                                    <div className="checkout-field checkout-field--full">
                                        <label htmlFor="street">{isRtl ? 'العنوان بالتفصيل (الشارع والحي ورقم المبنى) *' : t('FULL_ADDRESS')}</label>
                                        <input 
                                            id="street" 
                                            className="checkout-input" 
                                            name="street" 
                                            value={form.street} 
                                            onChange={handleChange} 
                                            autoComplete="street-address" 
                                            placeholder={isRtl ? 'مثال: طريق الملك فهد، حي العليا، برج رقم 4' : 'Street address, building, suite'} 
                                        />
                                    </div>
                                    <div className="checkout-field">
                                        <label htmlFor="city">{isRtl ? 'المدينة *' : (t('CITY') || 'City')}</label>
                                        <input 
                                            id="city" 
                                            className="checkout-input" 
                                            name="city" 
                                            value={form.city} 
                                            onChange={handleChange} 
                                            autoComplete="address-level2" 
                                            placeholder={isRtl ? 'مثال: الرياض / دبي / نيويورك' : 'e.g. New York / Riyadh'} 
                                        />
                                    </div>
                                    <div className="checkout-field">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                            <label htmlFor="country" style={{ margin: 0 }}>{isRtl ? 'الدولة والوجهة *' : (t('COUNTRY') || 'Country')}</label>
                                            <span style={{ fontSize: 11.5, color: '#FFFFFF', fontWeight: 600 }}>
                                                {isRtl ? 'تتغير طرق الدفع المتاحة تلقائياً حسب الدولة' : 'Payment methods adapt to your country'}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            <select
                                                value={activeCountryCode}
                                                onChange={e => {
                                                    const code = e.target.value;
                                                    const info = COUNTRY_CODE_TO_LABEL[code];
                                                    const name = isRtl ? (info?.nameAr || code) : (info?.nameEn || code);
                                                    setForm(prev => ({ ...prev, country: name }));
                                                }}
                                                className="checkout-input"
                                                style={{ width: 'auto', minWidth: 175, fontWeight: 700, cursor: 'pointer', background: '#0f172a', color: '#fff', border: '1.5px solid rgba(255, 255, 255,0.4)' }}
                                            >
                                                <option value="SA">🇸🇦 السعودية (SA)</option>
                                                <option value="AE">🇦🇪 الإمارات (AE)</option>
                                                <option value="EG">🇪🇬 مصر (EG)</option>
                                                <option value="JO">🇯🇴 الأردن (JO)</option>
                                                <option value="KW">🇰🇼 الكويت (KW)</option>
                                                <option value="QA">🇶🇦 قطر (QA)</option>
                                                <option value="BH">🇧🇭 البحرين (BH)</option>
                                                <option value="OM">🇴🇲 عمان (OM)</option>
                                                <option value="US">🇺🇸 أمريكا (US)</option>
                                                <option value="GB">🇬🇧 بريطانيا (GB)</option>
                                                <option value="DE">🇩🇪 ألمانيا (DE)</option>
                                                <option value="FR">🇫🇷 فرنسا (FR)</option>
                                            </select>
                                            <input 
                                                id="country" 
                                                className="checkout-input" 
                                                name="country" 
                                                value={form.country} 
                                                onChange={handleChange} 
                                                autoComplete="country-name" 
                                                placeholder={isRtl ? 'المملكة العربية السعودية / United States' : 'Country name'} 
                                                style={{ flex: 1, minWidth: 200 }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="checkout-trust-grid" style={{ marginTop: '24px' }}>
                                    <div className="checkout-trust-card">
                                        <strong><Truck size={14} style={{ verticalAlign: 'middle', marginInlineEnd: '8px' }} /> {isRtl ? 'موعد التوصيل التقديري' : 'Delivery estimate'}</strong>
                                        <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
                                            {isRtl ? 'الموعد المجدول للتسليم:' : 'Premium orders are scheduled for:'} <strong style={{ color: 'var(--lux-ink)', unicodeBidi: 'plaintext' }}>{deliveryWindow.display}</strong>
                                        </p>
                                    </div>
                                    <div className="checkout-trust-card">
                                        <strong><ShieldCheck size={14} style={{ verticalAlign: 'middle', marginInlineEnd: '8px' }} /> {isRtl ? 'حماية وضمان الطلب' : 'Trust protection'}</strong>
                                        <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
                                            {isRtl ? 'طلبكم مؤمن بالكامل ومحمي ببروتوكولات الشحن والتتبع المباشر.' : 'Your order remains tied to tracked fulfillment and secure payment handling.'}
                                        </p>
                                    </div>
                                </div>
                            </section>
                        )}

                        {currentStep === 'payment' && (
                            <section className="checkout-panel">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
                                    <h2 style={{ margin: 0 }}>{isRtl ? 'طريقة الدفع الفاخرة' : t('PAYMENT_METHOD')}</h2>
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, background: 'rgba(255, 255, 255,0.12)', border: '1px solid rgba(255, 255, 255,0.3)', fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>
                                        <span>{COUNTRY_CODE_TO_LABEL[activeCountryCode]?.flag || '🌍'}</span>
                                        <span>{isRtl ? (COUNTRY_CODE_TO_LABEL[activeCountryCode]?.nameAr || form.country) : (COUNTRY_CODE_TO_LABEL[activeCountryCode]?.nameEn || form.country)}</span>
                                    </div>
                                </div>
                                <p className="checkout-panel-copy">
                                    {isRtl ? 'تم تخصيص خيارات الدفع المعتمدة وفقاً لوجهتك المحددة لضمان أقصى درجات الأمان والسهولة.' : 'Payment options are automatically customized for your country to ensure seamless and secure settlement.'}
                                </p>

                                <div className="payment-method-stack">
                                    {/* 1. Mada (مدى) - Only for SA or when forced */}
                                    {Boolean(activePaymentConfig.enableMada && (activeCountryCode === 'SA' || activePaymentConfig.enableMada === true)) && (
                                        <label className={`payment-method-box ${form.paymentMethod === 'MADA' ? 'active' : ''}`} style={{ cursor: 'pointer' }}>
                                            <input type="radio" name="paymentMethod" value="MADA" checked={form.paymentMethod === 'MADA'} onChange={handleChange} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                                    <b style={{ fontSize: 15 }}>{isRtl ? 'بطاقة مدى البنكية (Mada Debit)' : 'Mada Debit Card'}</b>
                                                    <span style={{ fontSize: 12, background: 'rgba(16, 185, 129, 0.12)', color: '#10b981', padding: '3px 10px', borderRadius: 8, fontWeight: 'bold' }}>💳 مدى • معتمدة بنكياً</span>
                                                </div>
                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{isRtl ? 'دفع إلكتروني مباشر عبر شبكة مدى الوطنية لجميع البنوك السعودية.' : 'Direct payment via Saudi national Mada payment network.'}</span>
                                            </div>
                                        </label>
                                    )}

                                    {/* 2. Apple Pay (أبل باي) */}
                                    {activePaymentConfig.enableApplePay !== false && (
                                        <label className={`payment-method-box ${form.paymentMethod === 'APPLE_PAY' ? 'active' : ''}`} style={{ cursor: 'pointer' }}>
                                            <input type="radio" name="paymentMethod" value="APPLE_PAY" checked={form.paymentMethod === 'APPLE_PAY'} onChange={handleChange} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                                    <b style={{ fontSize: 15 }}>{isRtl ? 'أبل باي (Apple Pay)' : 'Apple Pay'}</b>
                                                    <span style={{ fontSize: 12, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '3px 10px', borderRadius: 8, fontWeight: 'bold' }}>🍏 Pay</span>
                                                </div>
                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{isRtl ? 'إتمام سريع وآمن بلمسة واحدة باستخدام Face ID أو Touch ID.' : 'Instant 1-touch checkout with Touch ID or Face ID.'}</span>
                                            </div>
                                        </label>
                                    )}

                                    {/* 3. Tabby (تابي - 4 دفعات) */}
                                    {Boolean(activePaymentConfig.tabbyEnabled !== false && (activeCountryCode === 'SA' || activeCountryCode === 'AE' || activePaymentConfig.tabbyEnabled === true)) && (
                                        <label className={`payment-method-box ${form.paymentMethod === 'TABBY' ? 'active' : ''}`} style={{ cursor: 'pointer', border: form.paymentMethod === 'TABBY' ? '2px solid #3efe9e' : undefined }}>
                                            <input type="radio" name="paymentMethod" value="TABBY" checked={form.paymentMethod === 'TABBY'} onChange={handleChange} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                                    <b style={{ fontSize: 15 }}>{isRtl ? 'تابي | قسّمها على 4 دفعات (Tabby)' : 'Tabby | Split in 4 Interest-Free'}</b>
                                                    <span style={{ fontSize: 12, background: 'rgba(62, 254, 158, 0.15)', color: '#00d27a', padding: '3px 10px', borderRadius: 8, fontWeight: 'bold' }}>🛍️ تابي 4 دفعات</span>
                                                </div>
                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                                                    {isRtl 
                                                        ? `ادفع ${(total / 4).toFixed(2)} الآن والباقي على 3 أشهر بدون فوائد أو رسوم خفية.` 
                                                        : `Pay ${(total / 4).toFixed(2)} today and the rest over 3 months with zero interest.`}
                                                </span>
                                            </div>
                                        </label>
                                    )}

                                    {/* 4. Tamara (تمارا - 3/4 دفعات) */}
                                    {Boolean(activePaymentConfig.tamaraEnabled !== false && (activeCountryCode === 'SA' || activeCountryCode === 'AE' || activePaymentConfig.tamaraEnabled === true)) && (
                                        <label className={`payment-method-box ${form.paymentMethod === 'TAMARA' ? 'active' : ''}`} style={{ cursor: 'pointer', border: form.paymentMethod === 'TAMARA' ? '2px solid #ff7b54' : undefined }}>
                                            <input type="radio" name="paymentMethod" value="TAMARA" checked={form.paymentMethod === 'TAMARA'} onChange={handleChange} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                                    <b style={{ fontSize: 15 }}>{isRtl ? 'تمارا | قسّم مشترياتك أو ادفع لاحقاً (Tamara)' : 'Tamara | Split into 3/4 or Pay Later'}</b>
                                                    <span style={{ fontSize: 12, background: 'rgba(255, 123, 84, 0.15)', color: '#ff7b54', padding: '3px 10px', borderRadius: 8, fontWeight: 'bold' }}>💎 تمارا</span>
                                                </div>
                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                                                    {isRtl 
                                                        ? `قسّم فاتورتك على 3 دفعات بقيمة ${(total / 3).toFixed(2)} أو ادفع بعد 30 يوماً بكل مرونة.` 
                                                        : `Split your purchase into 3 payments of ${(total / 3).toFixed(2)} or pay in 30 days.`}
                                                </span>
                                            </div>
                                        </label>
                                    )}

                                    {/* 5. Global Credit / Debit Card */}
                                    {activePaymentConfig.cardGatewayEnabled !== false && activePaymentConfig.enableVisaMastercard !== false && (
                                        <label className={`payment-method-box ${form.paymentMethod === 'CARD_PAYMENT' ? 'active' : ''}`} style={{ cursor: 'pointer' }}>
                                            <input type="radio" name="paymentMethod" value="CARD_PAYMENT" checked={form.paymentMethod === 'CARD_PAYMENT'} onChange={handleChange} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                                    <b style={{ fontSize: 15 }}>{isRtl ? 'البطاقات الائتمانية الدولية (Visa / Mastercard)' : (t('CREDIT_DEBIT_CARD') || 'Credit / Debit Card')}</b>
                                                    <span style={{ fontSize: 12, color: 'var(--lux-gold, #FFFFFF)', fontWeight: 'bold' }}>💳 Visa • Mastercard</span>
                                                </div>
                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{isRtl ? 'دفع إلكتروني فوري ومؤمن بتشفير SSL عالي الأمان.' : (t('PAY_CARD_DESC') || 'Instant and secure payment via direct payment gateway')}</span>
                                            </div>
                                        </label>
                                    )}

                                    {(form.paymentMethod === 'CARD_PAYMENT' || form.paymentMethod === 'MADA') && (
                                        <div className="checkout-trust-card" style={{ background: 'rgba(255,255,255,0.03)', border: '1.5px solid rgba(255, 255, 255,0.35)', padding: 20, borderRadius: 16, marginBottom: 12 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <ShieldCheck size={18} color="#10b981" />
                                                    <span style={{ fontSize: 13, fontWeight: 'bold', color: '#10b981' }}>
                                                        {isRtl ? 'بوابة الدفع البنكية مشفرة ومؤمنة بالكامل 3D Secure' : 'Direct Bank Gateway SSL Encrypted (3D Secure)'}
                                                    </span>
                                                </div>
                                                <span dir="ltr" style={{ fontSize: 11, background: 'rgba(16,185,129,0.12)', color: '#10b981', padding: '3px 10px', borderRadius: 8, fontWeight: 'bold' }}>
                                                    🔒 256-bit SSL Gateway
                                                </span>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                                <div style={{ gridColumn: 'span 2' }}>
                                                    <label style={{ fontSize: 12, fontWeight: 'bold', display: 'block', marginBottom: 6 }}>
                                                        {isRtl ? 'اسم حامل البطاقة (Cardholder Name)' : 'Cardholder Name'}
                                                    </label>
                                                    <input
                                                        className="checkout-input"
                                                        type="text"
                                                        placeholder={isRtl ? 'الاسم كما هو مدون على البطاقة' : 'Full Name as on Card'}
                                                        value={cardDetails.cardholderName || form.customerName}
                                                        onChange={e => setCardDetails(c => ({ ...c, cardholderName: e.target.value }))}
                                                        autoComplete="cc-name"
                                                    />
                                                </div>
                                                <div style={{ gridColumn: 'span 2' }}>
                                                    <label style={{ fontSize: 12, fontWeight: 'bold', display: 'block', marginBottom: 6 }}>
                                                        {isRtl ? 'رقم البطاقة البنكية (Card Number)' : 'Card Number'}
                                                    </label>
                                                    <input
                                                        className="checkout-input"
                                                        type="text"
                                                        placeholder="4242 4242 4242 4242"
                                                        maxLength="19"
                                                        dir="ltr"
                                                        style={{ fontFamily: 'monospace', fontSize: 15, letterSpacing: '0.08em' }}
                                                        value={cardDetails.cardNumber}
                                                        onChange={e => setCardDetails(c => ({ ...c, cardNumber: formatCardNumber(e.target.value) }))}
                                                        autoComplete="cc-number"
                                                    />
                                                </div>
                                                <div>
                                                    <label style={{ fontSize: 12, fontWeight: 'bold', display: 'block', marginBottom: 6 }}>
                                                        {isRtl ? 'تاريخ الانتهاء (MM/YY)' : 'Expiry Date (MM/YY)'}
                                                    </label>
                                                    <input
                                                        className="checkout-input"
                                                        type="text"
                                                        placeholder="MM / YY"
                                                        maxLength="5"
                                                        dir="ltr"
                                                        style={{ fontFamily: 'monospace', fontSize: 14 }}
                                                        value={cardDetails.expiry}
                                                        onChange={e => setCardDetails(c => ({ ...c, expiry: formatExpiryDate(e.target.value) }))}
                                                        autoComplete="cc-exp"
                                                    />
                                                </div>
                                                <div>
                                                    <label style={{ fontSize: 12, fontWeight: 'bold', display: 'block', marginBottom: 6 }}>
                                                        {isRtl ? 'رمز الأمان السري (CVV / CVC)' : 'Security Code (CVV / CVC)'}
                                                    </label>
                                                    <input
                                                        className="checkout-input"
                                                        type="password"
                                                        placeholder="•••"
                                                        maxLength="4"
                                                        dir="ltr"
                                                        style={{ fontFamily: 'monospace', fontSize: 14 }}
                                                        value={cardDetails.cvv}
                                                        onChange={e => setCardDetails(c => ({ ...c, cvv: e.target.value.replace(/\D/g, '') }))}
                                                        autoComplete="cc-csc"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* 6. Mobile Wallets / Phone Transfer */}
                                    {Boolean(activePaymentConfig.walletEnabled) && (
                                        <label className={`payment-method-box ${form.paymentMethod === 'WALLET_PAYMENT' ? 'active' : ''}`} style={{ cursor: 'pointer' }}>
                                            <input type="radio" name="paymentMethod" value="WALLET_PAYMENT" checked={form.paymentMethod === 'WALLET_PAYMENT'} onChange={handleChange} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                                    <b style={{ fontSize: 15 }}>
                                                        {activeCountryCode === 'EG'
                                                            ? (isRtl ? 'المحافظ الرقمية المصرية (فودافون كاش / إنستاباي)' : 'Egyptian Wallets (Vodafone Cash / InstaPay)')
                                                            : activeCountryCode === 'SA'
                                                            ? (isRtl ? 'المحافظ الرقمية السعودية (STC Pay / Urpay)' : 'Saudi Digital Wallets (STC Pay / Urpay)')
                                                            : activeCountryCode === 'JO'
                                                            ? (isRtl ? 'كليك والمحافظ الرقمية (CliQ)' : 'CliQ & Mobile Wallets')
                                                            : (isRtl ? 'التحويل عبر المحافظ الرقمية ورقم الهاتف' : (t('MOBILE_WALLET') || 'Mobile Wallets & Phone Transfer'))}
                                                    </b>
                                                    <span style={{ fontSize: 12, background: 'rgba(255, 255, 255, 0.15)', color: '#FFFFFF', padding: '3px 10px', borderRadius: 8, fontWeight: 'bold' }}>
                                                        📱 {isRtl ? 'تحويل فوري' : 'Direct Mobile'}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                                                    {isRtl ? 'التحويل المباشر عبر أرقام المحافظ المعتمدة ورفع إشعار التحويل لتأكيد الطلب.' : 'Direct transfer to authorized mobile wallet numbers and upload confirmation.'}
                                                </span>
                                            </div>
                                        </label>
                                    )}

                                    {activePaymentConfig.walletEnabled && form.paymentMethod === 'WALLET_PAYMENT' && (
                                        <div className="checkout-trust-card" style={{ background: 'rgba(255,255,255,0.03)', border: '1.5px solid rgba(255, 255, 255,0.3)', padding: 18, borderRadius: 14, marginBottom: 12 }}>
                                            <strong style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: '#FFFFFF' }}>
                                                <Wallet size={16} /> 
                                                <span>{isRtl ? 'أرقام المحافظ المعتمدة للتحويل:' : 'Authorized Wallet Destinations:'}</span>
                                            </strong>
                                            
                                            {activePaymentConfig.wallets && activePaymentConfig.wallets.length > 0 ? (
                                                <div style={{ display: 'grid', gap: 10 }}>
                                                    {activePaymentConfig.wallets.map((wallet, index) => (
                                                        <div key={`${wallet.provider}-${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '10px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)' }}>
                                                            <div>
                                                                <div style={{ fontWeight: 700, color: 'var(--lux-ink, #fff)', fontSize: 14 }}>{wallet.provider}</div>
                                                                <div dir="ltr" style={{ color: '#FFFFFF', fontWeight: 700, fontSize: 15, fontFamily: 'monospace', marginTop: 2 }}>{wallet.number}</div>
                                                                {wallet.holder && (
                                                                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isRtl ? 'اسم المستلم:' : 'Holder:'} {wallet.holder}</div>
                                                                )}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => copyToClipboard(wallet.number, `wallet-${index}`)}
                                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255, 255, 255,0.3)', background: copiedKey === `wallet-${index}` ? '#10b981' : 'rgba(255, 255, 255,0.1)', color: copiedKey === `wallet-${index}` ? '#fff' : '#FFFFFF', cursor: 'pointer', fontSize: 12, fontWeight: 700, transition: 'all 0.2s ease' }}
                                                            >
                                                                {copiedKey === `wallet-${index}` ? <Check size={14} /> : <Copy size={14} />}
                                                                <span>{copiedKey === `wallet-${index}` ? (isRtl ? 'تم النسخ' : 'Copied') : (isRtl ? 'نسخ الرقم' : 'Copy')}</span>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>{isRtl ? 'يرجى التواصل مع خدمة العملاء للحصول على رقم المحفظة المحدث.' : 'Please contact support for the latest wallet details.'}</p>
                                            )}

                                            <div className="checkout-field" style={{ marginTop: '16px' }}>
                                                <label htmlFor="walletReceiptFile" style={{ fontSize: 13, fontWeight: 700 }}>
                                                    {isRtl ? 'إرفاق إشعار تحويل المحفظة (اختياري / يُنصح به لسرعة التأكيد)' : 'Upload Wallet Transfer Receipt (Recommended)'}
                                                </label>
                                                <input id="walletReceiptFile" className="checkout-input" type="file" accept="image/*" onChange={handleFileChange} />
                                            </div>
                                        </div>
                                    )}

                                    {/* 7. Direct Bank Wire Transfer */}
                                    {Boolean(activePaymentConfig.bankTransferEnabled) && (
                                        <label className={`payment-method-box ${form.paymentMethod === 'BANK_TRANSFER' ? 'active' : ''}`} style={{ cursor: 'pointer' }}>
                                            <input type="radio" name="paymentMethod" value="BANK_TRANSFER" checked={form.paymentMethod === 'BANK_TRANSFER'} onChange={handleChange} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                                    <b style={{ fontSize: 15 }}>
                                                        {isRtl 
                                                            ? `التحويل البنكي المباشر (${COUNTRY_CODE_TO_LABEL[activeCountryCode]?.nameAr || form.country})`
                                                            : `Direct Bank Transfer (${COUNTRY_CODE_TO_LABEL[activeCountryCode]?.nameEn || form.country})`}
                                                    </b>
                                                    <span style={{ fontSize: 12, background: 'rgba(16, 185, 129, 0.12)', color: '#10b981', padding: '3px 10px', borderRadius: 8, fontWeight: 'bold' }}>
                                                        🏛️ {isRtl ? 'حساب بنكي' : 'Bank Wire'}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                                                    {isRtl ? 'تحويل بنكي مباشر عبر الآيبان والحسابات المعتمدة ثم إرفاق الإشعار لتأكيد الطلب.' : (t('PAY_BANK_DESC') || 'Direct wire transfer to bank accounts with receipt upload.')}
                                                </span>
                                            </div>
                                        </label>
                                    )}

                                    {activePaymentConfig.bankTransferEnabled && form.paymentMethod === 'BANK_TRANSFER' && (
                                        <div className="checkout-trust-card" style={{ background: 'rgba(255,255,255,0.03)', border: '1.5px solid rgba(255, 255, 255,0.3)', padding: 18, borderRadius: 14, marginBottom: 12 }}>
                                            <strong style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: '#FFFFFF' }}>
                                                <MapPin size={16} />
                                                <span>{isRtl ? 'تفاصيل الحسابات البنكية المعتمدة:' : 'Approved Bank Accounts:'}</span>
                                            </strong>

                                            {activePaymentConfig.banks && activePaymentConfig.banks.length > 0 ? (
                                                <div style={{ display: 'grid', gap: 12 }}>
                                                    {activePaymentConfig.banks.map((bank, index) => (
                                                        <div key={`${bank.account}-${index}`} style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)' }}>
                                                            <div style={{ color: 'var(--lux-ink, #fff)', fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{bank.name}</div>
                                                            
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, margin: '4px 0' }}>
                                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{isRtl ? 'رقم الحساب:' : 'Account Number:'} <b dir="ltr" style={{ color: '#FFFFFF', fontFamily: 'monospace' }}>{bank.account}</b></span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => copyToClipboard(bank.account, `acc-${index}`)}
                                                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: '1px solid rgba(255, 255, 255,0.3)', background: copiedKey === `acc-${index}` ? '#10b981' : 'transparent', color: copiedKey === `acc-${index}` ? '#fff' : '#FFFFFF', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}
                                                                >
                                                                    {copiedKey === `acc-${index}` ? <Check size={12} /> : <Copy size={12} />}
                                                                    <span>{copiedKey === `acc-${index}` ? (isRtl ? 'تم' : 'Copied') : (isRtl ? 'نسخ' : 'Copy')}</span>
                                                                </button>
                                                            </div>

                                                            {bank.iban && (
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, margin: '4px 0' }}>
                                                                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{isRtl ? 'الآيبان الدولي (IBAN):' : 'IBAN:'} <b dir="ltr" style={{ color: 'var(--lux-ink, #fff)', fontFamily: 'monospace', letterSpacing: '0.04em' }}>{bank.iban}</b></span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => copyToClipboard(bank.iban, `iban-${index}`)}
                                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: '1px solid rgba(255, 255, 255,0.3)', background: copiedKey === `iban-${index}` ? '#10b981' : 'transparent', color: copiedKey === `iban-${index}` ? '#fff' : '#FFFFFF', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}
                                                                    >
                                                                        {copiedKey === `iban-${index}` ? <Check size={12} /> : <Copy size={12} />}
                                                                        <span>{copiedKey === `iban-${index}` ? (isRtl ? 'تم' : 'Copied') : (isRtl ? 'نسخ' : 'Copy')}</span>
                                                                    </button>
                                                                </div>
                                                            )}

                                                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                                                                {isRtl ? 'اسم المستفيد:' : 'Beneficiary:'} <strong style={{ color: 'var(--lux-ink, #fff)' }}>{bank.holder}</strong>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>{isRtl ? 'يرجى التواصل مع خدمة العملاء للحصول على الحسابات البنكية المحدثة.' : 'No bank accounts registered.'}</p>
                                            )}

                                            <div className="checkout-field" style={{ marginTop: '16px' }}>
                                                <label htmlFor="receiptFile" style={{ fontSize: 13, fontWeight: 700 }}>
                                                    {isRtl ? 'إرفاق إشعار التحويل البنكي (مطلوب لتأكيد الطلب) *' : (t('UPLOAD_RECEIPT') || 'Upload Bank Wire Receipt (Required) *')}
                                                </label>
                                                <input id="receiptFile" className="checkout-input" type="file" accept="image/*" onChange={handleFileChange} />
                                            </div>
                                        </div>
                                    )}

                                    {/* 8. PayPal */}
                                    {Boolean(activePaymentConfig.paypalEnabled) && (
                                        <label className={`payment-method-box ${form.paymentMethod === 'PAYPAL' ? 'active' : ''}`} style={{ cursor: 'pointer' }}>
                                            <input type="radio" name="paymentMethod" value="PAYPAL" checked={form.paymentMethod === 'PAYPAL'} onChange={handleChange} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                                    <b style={{ display: 'block', fontSize: 15 }}>{isRtl ? 'بوابة باي بال (PayPal Gateway)' : 'PayPal Gateway'}</b>
                                                    <span style={{ fontSize: 12, color: '#0079C1', fontWeight: 'bold' }}>PayPal</span>
                                                </div>
                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{isRtl ? 'الدفع الآمن والسريع عبر حساب باي بال العالمي.' : 'Fast and secured checkout via PayPal.'}</span>
                                            </div>
                                        </label>
                                    )}

                                    {/* 9. Cash on Delivery (COD) */}
                                    {Boolean(activePaymentConfig.codEnabled) && (
                                        <label className={`payment-method-box ${form.paymentMethod === 'COD' ? 'active' : ''}`} style={{ cursor: 'pointer' }}>
                                            <input type="radio" name="paymentMethod" value="COD" checked={form.paymentMethod === 'COD'} onChange={handleChange} />
                                            <div style={{ flex: 1 }}>
                                                <b style={{ display: 'block', fontSize: 15 }}>{isRtl ? 'الدفع عند الاستلام (Cash on Delivery)' : t('CASH_ON_DELIVERY')}</b>
                                                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{isRtl ? 'سداد نقدي عند التوصيل الشخصي الفاخر مع مندوب الكونسيرج الخاص بنا.' : t('PAY_CASH_DESC')}</span>
                                            </div>
                                        </label>
                                    )}
                                </div>
                            </section>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '24px' }}>
                            {currentStep !== 'details' ? (
                                <button type="button" className="hero-secondary-link" onClick={() => setCurrentStep(STEPS[Math.max(0, STEPS.findIndex(step => step.id === currentStep) - 1)].id)}>
                                    {isRtl ? '← السابق' : 'Back'}
                                </button>
                            ) : <span />}

                            {currentStep !== 'payment' ? (
                                <button type="button" className="btn-primary" onClick={goToNextStep}>
                                    {isRtl ? (currentStep === 'details' ? 'متابعة إلى الشحن والتوصيل →' : 'متابعة إلى خيارات الدفع →') : 'Continue'}
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    className="btn-primary btn-lg"
                                    disabled={submitting || !form.paymentMethod}
                                    style={{ justifyContent: 'center', gap: 10 }}
                                >
                                    {submitting && <span className="spin" style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%' }} />}
                                    {submitting ? (isRtl ? 'جاري معالجة الطلب...' : t('PROCESSING_ORDER')) : `${isRtl ? 'تأكيد ودفع الطلب الفاخر' : t('PLACE_ORDER')} • $${total.toLocaleString()}`}
                                </button>
                            )}
                        </div>
                    </form>

                    <aside className="checkout-summary-card">
                        <h2>{t('ORDER_SUMMARY')}</h2>
                        <div className="checkout-summary-items">
                            {cart.map(item => (
                                <div key={item.id} className="checkout-summary-item">
                                    <img src={item.imageUrl || item.image || item.primaryImageUrl || '/product-fallback.svg'} alt={item.productName || item.name || 'Product'} />
                                    <div>
                                        <h4>{item.productName || item.name || item.title || 'Unknown Product'}</h4>
                                        <div className="checkout-summary-meta">{t('QTY')}: {item.quantity || item.qty || 1}</div>
                                    </div>
                                    <div style={{ fontWeight: 800, color: '#e02424', fontSize: '15px' }}>
                                        ${((item.price || item.currentPrice || 0) * (item.quantity || item.qty || 1)).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="checkout-totals">
                            <div className="checkout-total-row">
                                <span>{t('SUBTOTAL')}</span>
                                <span style={{ fontWeight: 600, color: '#111827' }}>${subtotal.toLocaleString()}</span>
                            </div>

                            {/* Golden Coupon Discount Row */}
                            {appliedCoupon && discountAmount > 0 && (
                                <div className="checkout-total-row" style={{
                                    color: '#FFFFFF',
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    border: '1px dashed #FFFFFF',
                                    padding: '6px 12px',
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    margin: '4px 0'
                                }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Sparkles size={13} />
                                        {isRtl ? 'خصم القسيمة' : 'Coupon Discount'} ({appliedCoupon.code})
                                    </span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontWeight: 900 }}>-${discountAmount.toLocaleString()}</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setAppliedCoupon(null);
                                                localStorage.removeItem('luxvono_active_coupon');
                                            }}
                                            style={{
                                                background: 'rgba(239, 68, 68, 0.15)',
                                                border: 'none',
                                                color: '#f87171',
                                                borderRadius: '50%',
                                                width: 18,
                                                height: 18,
                                                cursor: 'pointer',
                                                fontSize: 10,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            title="إزالة الكوبون"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="checkout-total-row">
                                <span>{t('SHIPPING')}</span>
                                <span>{(shipping === 0 || appliedCoupon?.discountType === 'FREE_SHIPPING') ? (isRtl ? 'مجاني' : t('COMPLIMENTARY')) : `$${shipping.toLocaleString()}`}</span>
                            </div>
                            <div className="checkout-total-row">
                                <span>{t('TAX')}</span>
                                <span>{estimatedTax === 0 ? '$0.00' : `$${estimatedTax.toLocaleString()}`}</span>
                            </div>
                            <div className="checkout-total-row checkout-total" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.25)', paddingTop: 12 }}>
                                <span style={{ fontWeight: 800, color: '#111827', fontSize: '16px' }}>{t('TOTAL')}</span>
                                <span style={{ color: '#FFFFFF', fontWeight: 900, fontSize: '1.35rem' }}>${total.toLocaleString()}</span>
                            </div>

                            {/* Direct Coupon Input in Checkout */}
                            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <input
                                        type="text"
                                        placeholder={isRtl ? "كود الخصم (Promo Code)" : "Enter promo code"}
                                        value={checkoutCouponCode}
                                        onChange={(e) => setCheckoutCouponCode(e.target.value.toUpperCase())}
                                        style={{
                                            flex: 1,
                                            background: '#f9fafb',
                                            border: '1px solid #d1d5db',
                                            borderRadius: 8,
                                            padding: '7px 10px',
                                            fontSize: '0.82rem',
                                            fontWeight: 700,
                                            letterSpacing: '1px'
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (!checkoutCouponCode.trim()) return;
                                            const code = checkoutCouponCode.trim().toUpperCase();
                                            try {
                                                const res = await validateCoupon(code, subtotal);
                                                if (res && res.valid) {
                                                    const validCoupon = {
                                                        code: res.code,
                                                        title: res.description || res.code,
                                                        discountType: res.discountType,
                                                        discountValue: res.discountValue,
                                                        discountAmount: Number(res.discountAmount) || 0,
                                                        minOrderAmount: res.minOrderAmount
                                                    };
                                                    setAppliedCoupon(validCoupon);
                                                    localStorage.setItem('luxvono_active_coupon', JSON.stringify(validCoupon));
                                                    setCouponFeedback({
                                                        isError: false,
                                                        text: res.message || (isRtl ? `تم تطبيق كود (${code}) بنجاح!` : `Applied ${code}!`)
                                                    });
                                                    setCheckoutCouponCode('');
                                                } else {
                                                    setCouponFeedback({
                                                        isError: true,
                                                        text: res?.message || (isRtl ? `كود الخصم (${code}) غير صالح أو منتهي.` : `Coupon (${code}) is invalid.`)
                                                    });
                                                }
                                            } catch (err) {
                                                setCouponFeedback({
                                                    isError: true,
                                                    text: isRtl ? `تعذر التحقق من كود (${code}).` : `Unable to validate coupon (${code}).`
                                                });
                                            }
                                        }}
                                        style={{
                                            background: 'linear-gradient(135deg, #FFFFFF 0%, #E2E8F0 100%)',
                                            color: '#000',
                                            border: 'none',
                                            borderRadius: 8,
                                            padding: '0 12px',
                                            fontSize: '0.8rem',
                                            fontWeight: 700,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {isRtl ? 'تطبيق' : 'Apply'}
                                    </button>
                                </div>
                                {couponFeedback && (
                                    <span style={{ 
                                        color: (typeof couponFeedback === 'object' && couponFeedback?.isError) ? '#f87171' : '#10b981', 
                                        fontSize: '0.75rem', 
                                        fontWeight: 600, 
                                        display: 'block', 
                                        marginTop: 4 
                                    }}>
                                        {typeof couponFeedback === 'object' ? couponFeedback.text : couponFeedback}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="checkout-trust-grid">
                            <div className="checkout-trust-card">
                                <strong>{isRtl ? 'موعد التوصيل' : 'Delivery window'}</strong>
                                <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
                                    {isRtl ? 'الموعد المجدول:' : 'Estimated arrival:'} <strong style={{ color: 'var(--lux-ink)', unicodeBidi: 'plaintext' }}>{deliveryWindow.display}</strong>
                                </p>
                            </div>
                            <div className="checkout-trust-card">
                                <strong>{isRtl ? 'حماية وضمان المشتريات' : 'Customer protection'}</strong>
                                <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
                                    {isRtl ? 'مغطى بحماية المعاملات المشفرة ودعم الكونسيرج.' : 'Orders remain covered by secure payment handling and premium support.'}
                                </p>
                            </div>
                        </div>
                    </aside>
                </div>

                <DynamicSectionsRenderer
                    page="checkout-page"
                    position="bottom"
                    storeSettings={storeSettings}
                    allProducts={allProducts}
                    allCategories={allCategories}
                />
                <DynamicSectionsRenderer
                    page="checkout-page"
                    position="before-footer"
                    storeSettings={storeSettings}
                    allProducts={allProducts}
                    allCategories={allCategories}
                />
            </div>
        </div>
    );
};

export default CheckoutPage;
